import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import {
  Shield, AlertTriangle, FileText, Bug, TrendingUp, TrendingDown,
  Activity, Clock, CheckCircle2, XCircle, AlertCircle, FileDown, Loader2
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { TEXT_TONE } from "../components/ui/status-badge";
import { getScoreTone, getStatusHex } from "../lib/statusTokens";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import CloudPostureWidget from "../components/CloudPostureWidget";
import { DashboardSkeleton } from "../components/skeletons";
import { HeroScore } from "../components/dashboard/HeroScore";
import { NextActions } from "../components/dashboard/NextActions";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from "recharts";

const Dashboard = () => {
  const { token, user } = useAuth();
  const { t, locale } = useI18n();
  const [stats, setStats] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [healthScore, setHealthScore] = useState(null);
  const [nextActions, setNextActions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const isES = locale === "es";

  // Friendly time-of-day greeting for the hero card.
  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return t("dashboard.greeting_morning");
    if (hour < 18) return t("dashboard.greeting_afternoon");
    return t("dashboard.greeting_evening");
  })();
  const heroGreeting = `${greeting}, ${user?.name?.split(" ")[0] || t("dashboard.greeting_fallback")}`;

  const exportExecutivePdf = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/dashboard/executive-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `executive_report_${ts}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t("dashboard.export_success"));
    } catch (e) {
      toast.error(t("dashboard.export_failed"));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchTrendData();
    fetchHealthScore();
    fetchNextActions();
  }, [token]);

  const fetchNextActions = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/next-actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNextActions(response.data?.items || []);
    } catch (error) {
      setNextActions([]);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthScore = async () => {
    try {
      const response = await axios.get(`${API}/health-score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealthScore(response.data);
    } catch (error) {
      console.error("Failed to fetch health score:", error);
    }
  };

  const fetchTrendData = async () => {
    try {
      const response = await axios.get(`${API}/compliance-scores/trend?days=90`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = response.data || [];
      const grouped = {};
      raw.forEach(entry => {
        const date = entry.recorded_at?.substring(0, 10) || "unknown";
        if (!grouped[date]) grouped[date] = { date };
        grouped[date][entry.framework] = entry.score;
      });
      setTrendData(Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date)));
    } catch (error) {
      console.error("Failed to fetch trend data:", error);
    }
  };

  const frameworkScores = stats?.compliance_scores ? [
    { name: "ISO 27001", score: stats.compliance_scores.iso27001 || 0, color: "#3B82F6" },
    { name: "NIST SCF", score: stats.compliance_scores.nist_scf || 0, color: "#A855F7" },
    { name: "CIS v8.1", score: stats.compliance_scores.cis_v8 || 0, color: "#F97316" },
    { name: "ISO 20000", score: stats.compliance_scores.iso20000 || 0, color: "#0D9488" }
  ] : [];

  const riskData = stats?.risk_summary ? [
    { name: t("critical"), value: stats.risk_summary.critical || 0, color: getStatusHex("risk", "critical") },
    { name: t("high"), value: stats.risk_summary.high || 0, color: getStatusHex("risk", "high") },
    { name: t("medium"), value: stats.risk_summary.medium || 0, color: getStatusHex("risk", "medium") },
    { name: t("low"), value: stats.risk_summary.low || 0, color: getStatusHex("risk", "low") }
  ] : [];

  // Incidentes P1-P4: escala de severidad de incidente, no de riesgo, pero comparte
  // la paleta critical/high/medium/low (p1-design.md §2, nota incidentes).
  const INCIDENT_TO_RISK = { P1: "critical", P2: "high", P3: "medium", P4: "low" };
  const incidentSeverity = stats?.incident_summary?.by_severity ? [
    { name: "P1", value: stats.incident_summary.by_severity.P1 || 0, color: getStatusHex("risk", INCIDENT_TO_RISK.P1) },
    { name: "P2", value: stats.incident_summary.by_severity.P2 || 0, color: getStatusHex("risk", INCIDENT_TO_RISK.P2) },
    { name: "P3", value: stats.incident_summary.by_severity.P3 || 0, color: getStatusHex("risk", INCIDENT_TO_RISK.P3) },
    { name: "P4", value: stats.incident_summary.by_severity.P4 || 0, color: getStatusHex("risk", INCIDENT_TO_RISK.P4) }
  ] : [];

  if (loading) {
    return <DashboardSkeleton label={t("dashboard.loading")} />;
  }

  // Month-over-month delta from trendData (last - first within window)
  const heroDelta = (() => {
    if (!trendData || trendData.length < 2) return 0;
    const recent = trendData.slice(-1)[0];
    const old = trendData[0];
    const overallRecent = (recent.iso27001 || 0 + recent.nist_scf || 0 + recent.cis_v8 || 0 + recent.iso20000 || 0) / 4;
    const overallOld = (old.iso27001 || 0 + old.nist_scf || 0 + old.cis_v8 || 0 + old.iso20000 || 0) / 4;
    return Math.round(overallRecent - overallOld);
  })();

  const heroScore = healthScore?.score ?? stats?.compliance_scores?.overall ?? 0;
  const heroSubtitle = healthScore?.grade
    ? `${t("dashboard.grade_label")} ${healthScore.grade}`
    : null;

  return (
    <div className="p-6 space-y-6" data-testid="dashboard">
      {/* Hero Score */}
      <HeroScore
        greeting={heroGreeting}
        score={heroScore}
        delta={heroDelta}
        subtitle={heroSubtitle}
        onExport={exportExecutivePdf}
        exporting={exporting}
        isES={isES}
      />

      {/* Cloud Security Posture (Pro/Enterprise only; hides if no scans) */}
      <CloudPostureWidget />

      {/* Next Actions + Health Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <NextActions
            items={nextActions || []}
            loading={nextActions === null}
            isES={isES}
          />
        </div>

        {/* Health Score breakdown (compact) */}
        {healthScore && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card className="border-border h-full" data-testid="health-score-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" aria-hidden="true" />
                  {t("dashboard.health")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(healthScore.breakdown || {}).map(([key, item]) => {
                    // Use the i18n key if defined; fall back to the backend's English label
                    const translated = t(`health.${key}`);
                    const label = translated && translated !== `health.${key}` ? translated : item.label;
                    return (
                      <div key={key} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground truncate pr-2">{label}</span>
                          <span className="font-mono font-medium">{item.score}</span>
                        </div>
                        <Progress value={item.score} className="h-1.5" aria-label={`${label} ${item.score}%`} />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* Framework Badges row (kept for at-a-glance scope) */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground uppercase tracking-wider mr-1">
          {t("dashboard.active_frameworks")}
        </span>
        <Badge variant="outline" className="badge-iso27001">ISO 27001</Badge>
        <Badge variant="outline" className="badge-nist">NIST SCF</Badge>
        <Badge variant="outline" className="badge-cis">CIS v8.1</Badge>
        <Badge variant="outline" className="badge-iso20000">ISO 20000</Badge>
      </div>

      {/* Bento Grid - Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Framework Scores */}
        {frameworkScores.map((fw, index) => (
          <motion.div
            key={fw.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <Card className="border-border hover:border-primary/50 transition-colors" data-testid={`framework-card-${fw.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fw.color }} />
                    <span className="text-sm font-medium">{fw.name}</span>
                  </div>
                  <span className={`text-2xl font-heading font-bold ${TEXT_TONE[getScoreTone(fw.score)]}`}>
                    {fw.score}%
                  </span>
                </div>
                <Progress value={fw.score} className="h-2" />
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card className="border-border" data-testid="risk-distribution-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                {t("ux.risk_distribution")}
              </CardTitle>
              <CardDescription>
                Breakdown by risk level
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riskData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {riskData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-center">
                <p className="text-2xl font-heading font-bold">
                  {stats?.risk_summary?.total || 0}
                </p>
                <p className="text-sm text-muted-foreground">{t("dashboard.total_risks")}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Incident Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <Card className="border-border" data-testid="incident-summary-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bug className="w-5 h-5 text-red-500" />
                {t("ux.incident_summary")}
              </CardTitle>
              <CardDescription>
                Active incidents by severity
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={incidentSeverity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {incidentSeverity.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center">
                  <p className="text-xl font-heading font-bold text-yellow-500">
                    {stats?.incident_summary?.new || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">{t("dashboard.new")}</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-heading font-bold text-blue-500">
                    {stats?.incident_summary?.investigating || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">{t("dashboard.investigating")}</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-heading font-bold text-green-500">
                    {stats?.incident_summary?.closed || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">{t("dashboard.closed")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Compliance Trend Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.55 }}
      >
        <Card className="border-border" data-testid="compliance-trend-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              {t("ux.compliance_score_trend")}
            </CardTitle>
            <CardDescription>
              {t("ux.compliance_score_trend_desc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trendData.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      stroke="hsl(var(--muted-foreground))"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(d) => {
                        const dt = new Date(d);
                        return `${dt.getMonth()+1}/${dt.getDate()}`;
                      }}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="iso27001" stroke="#3B82F6" name="ISO 27001" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="nist_scf" stroke="#A855F7" name="NIST SCF" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="cis_v8" stroke="#F97316" name="CIS v8.1" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="iso20000" stroke="#0D9488" name="ISO 20000" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Activity className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">{t("dashboard.trend_placeholder")}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <Card className="border-border" data-testid="documents-stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.documents")}</p>
                  <p className="text-3xl font-heading font-bold mt-1">
                    {stats?.document_summary?.total || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <div className="flex items-center gap-4 mt-4 text-sm">
                <span className="flex items-center gap-1 text-green-500">
                  <CheckCircle2 className="w-4 h-4" />
                  {stats?.document_summary?.published || 0} {t("ux.published")}
                </span>
                <span className="flex items-center gap-1 text-yellow-500">
                  <Clock className="w-4 h-4" />
                  {stats?.document_summary?.draft || 0} {t("ux.draft")}
                </span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.7 }}
        >
          <Card className="border-border" data-testid="assets-stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.assets_tracked")}</p>
                  <p className="text-3xl font-heading font-bold mt-1">
                    {stats?.asset_count || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-purple-500" />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-muted-foreground">
                  Hardware, Software, Data, Services
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.8 }}
        >
          <Card className="border-border" data-testid="status-stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.compliance_status")}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {(stats?.compliance_scores?.overall || 0) >= 70 ? (
                      <>
                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                        <span className="text-xl font-heading font-bold text-green-500">{t("dashboard.on_track")}</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-6 h-6 text-yellow-500" />
                        <span className="text-xl font-heading font-bold text-yellow-500">{t("dashboard.needs_attention")}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-muted-foreground">
                  Based on overall compliance score
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
